/**
 * Caps collection grid slot block height by mirroring a reference product card row height.
 * Sets on the grid element:
 * - --product-grid-measured-reference-item-block-size
 * - --product-grid-measured-row-gap
 *
 * @param {HTMLUListElement | null | undefined} grid
 * @returns {() => void}
 */
export function observeCollectionGridSlotMetrics(grid) {
  const SLOT_METRICS_LOG = '[slot-metrics]';
  if (!grid) {
    console.log(SLOT_METRICS_LOG, 'observeCollectionGridSlotMetrics: no grid element');
    return () => {};
  }

  console.log(SLOT_METRICS_LOG, 'observe attached', {
    tag: grid.tagName,
    className: grid.className,
    id: grid.id,
    childElementCount: grid.childElementCount,
  });

  /** @type {ResizeObserver | undefined} */
  let referenceResizeObserver;
  /** @type {HTMLElement | undefined} */
  let observedReference;

  let lastSetVarsLog = 0;
  let lastSkipReasonLog = 0;

  const gridResizeObserver = new ResizeObserver(() => scheduleUpdate());
  gridResizeObserver.observe(grid);

  const mutationObserver = new MutationObserver(() => scheduleUpdate());
  mutationObserver.observe(grid, { childList: true });

  let frame = 0;

  const scheduleUpdate = () => {
    if (frame) cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      frame = 0;
      updateMetrics();
    });
  };

  const disconnectReferenceObserver = () => {
    referenceResizeObserver?.disconnect();
    referenceResizeObserver = undefined;
    observedReference = undefined;
  };

  const reconnectReferenceObserverIfNeeded = (/** @type {HTMLElement} */ reference) => {
    if (observedReference === reference) return;
    disconnectReferenceObserver();
    observedReference = reference;
    referenceResizeObserver = new ResizeObserver(scheduleUpdate);
    referenceResizeObserver.observe(reference);
  };

  const updateMetrics = () => {
    const hasSlots = grid.querySelector('.product-grid__item--collection-slot');
    if (!hasSlots) {
      const t = performance.now();
      if (t - lastSkipReasonLog > 2000) {
        lastSkipReasonLog = t;
        console.log(SLOT_METRICS_LOG, 'updateMetrics: no .product-grid__item--collection-slot in grid');
      }
      disconnectReferenceObserver();
      grid.style.removeProperty('--product-grid-measured-reference-item-block-size');
      grid.style.removeProperty('--product-grid-measured-row-gap');
      return;
    }

    const reference =
      grid.querySelector('.product-grid__item--product') ??
      grid.querySelector('li.product-grid__item:not(.product-grid__item--collection-slot)');
    if (!reference || !(reference instanceof HTMLElement)) {
      const t = performance.now();
      if (t - lastSkipReasonLog > 2000) {
        lastSkipReasonLog = t;
        console.log(SLOT_METRICS_LOG, 'updateMetrics: no reference product row', {
          productSelectorCount: grid.querySelectorAll('.product-grid__item--product').length,
          itemLiCount: grid.querySelectorAll('li.product-grid__item').length,
        });
      }
      disconnectReferenceObserver();
      grid.style.removeProperty('--product-grid-measured-reference-item-block-size');
      grid.style.removeProperty('--product-grid-measured-row-gap');
      return;
    }

    reconnectReferenceObserverIfNeeded(reference);

    const styles = getComputedStyle(grid);
    const rowGap = parseFloat(styles.rowGap);
    const rowGapPx = Number.isFinite(rowGap) ? rowGap : 0;
    const height = Math.round(reference.getBoundingClientRect().height);

    grid.style.setProperty('--product-grid-measured-reference-item-block-size', `${height}px`);
    grid.style.setProperty('--product-grid-measured-row-gap', `${rowGapPx}px`);
    updateDetailsBlockSizes(reference);

    const now = performance.now();
    if (now - lastSetVarsLog > 2000) {
      lastSetVarsLog = now;
      console.log(SLOT_METRICS_LOG, 'updateMetrics: set vars (throttled 2s)', {
        heightPx: height,
        rowGapPx,
        rawRowGap: styles.rowGap,
      });
    }
  };

  /**
   * Height of the text block under a card's image (title, price, anything an app adds).
   * @param {Element} card
   */
  const detailsBlockSize = (card) => {
    const gallery = card.querySelector('.card-gallery');
    if (!gallery) return 0;
    return Math.max(0, card.getBoundingClientRect().bottom - gallery.getBoundingClientRect().bottom);
  };

  /**
   * For each slot, records the tallest text block among the cards sharing its last row, so a slot
   * set to end level with the product images can subtract exactly that. A slot with no card beside
   * it (it fills every column) falls back to the reference card's text block.
   *
   * Also flags a slot whose last row holds no card at all (a filtered or sorted result set, or a
   * small collection). The stylesheet lets such a slot mirror the reference card's height, because
   * nothing else sizes that row and the row would otherwise collapse to the slot's own text.
   * @param {HTMLElement} reference
   */
  const updateDetailsBlockSizes = (reference) => {
    const fallback = detailsBlockSize(reference);
    const cards = Array.from(grid.querySelectorAll(':scope > li:not(.product-grid__item--collection-slot)'));
    for (const slot of grid.querySelectorAll(':scope > li.product-grid__item--collection-slot')) {
      if (!(slot instanceof HTMLElement)) continue;
      const rect = slot.getBoundingClientRect();
      let size = 0;
      let found = false;
      for (const card of cards) {
        const cardRect = card.getBoundingClientRect();
        // A card shares the slot's last row when it starts within the slot's rows and reaches the slot's bottom.
        if (cardRect.top < rect.top - 1 || Math.abs(cardRect.bottom - rect.bottom) > 1) continue;
        found = true;
        size = Math.max(size, detailsBlockSize(card));
      }
      if (!found) size = fallback;
      slot.style.setProperty('--collection-grid-slot-details-block-size', `${Math.round(size * 10) / 10}px`);
      slot.style.setProperty('--collection-grid-slot-last-row-empty', found ? '0' : '1');
    }
  };

  scheduleUpdate();

  return () => {
    console.log(SLOT_METRICS_LOG, 'observe teardown', { className: grid.className });
    if (frame) cancelAnimationFrame(frame);
    gridResizeObserver.disconnect();
    mutationObserver.disconnect();
    disconnectReferenceObserver();
    grid.style.removeProperty('--product-grid-measured-reference-item-block-size');
    grid.style.removeProperty('--product-grid-measured-row-gap');
    for (const slot of grid.querySelectorAll(':scope > li.product-grid__item--collection-slot')) {
      if (!(slot instanceof HTMLElement)) continue;
      slot.style.removeProperty('--collection-grid-slot-details-block-size');
      slot.style.removeProperty('--collection-grid-slot-last-row-empty');
    }
  };
}
